import React, { useState } from "react";
import { useFormikContext } from "formik";
import orderBy from "lodash/orderBy";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import useQueryParams from "src/hooks/useQueryParams";

import ImageModal from "../../../components/images/ImageModal";
import useNotification from "../../../hooks/notification";
import { useAssets } from "../../../contexts/assets";
import ImageDND from "./ImageDND";
import ImagesSlider from "./ImagesSlider";
import ImageUploadButtons from "./ImageUploadButtons";
import {
  ADD_ATTACHMENT_SHIPMENT_ITEMS,
  REMOVE_ATTACHMENT_SHIPMENT_ITEMS,
  UPDATE_ATTACHMENT_SHIPMENT_ITEMS,
} from "src/constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import { useLoading } from "../../../components/capture/ReceivingWizard/Items/ItemDetails/LoadingProvider";
import { GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";
import { compressImageFiles } from "src/utils/imageCompression";

export default function ImagesItem({ asset }) {
  const { updateImage, bulkCreateImages, removeImage } = useAssets();
  const { notifyError, notifySuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const { errors = {}, submitCount = 0 } = useFormikContext();
  const params = useQueryParams();
  const shipmentItemId = params.get("shipmentItemId");

  // In receiving wizard context: ONLY show shipment item attachments (no fallback to asset images)
  const isAttachments = asset?.images?.[0]?.pairId !== undefined || shipmentItemId !== null;

  // If attachments: keep original order. If asset images: sort by default first
  const list = isAttachments 
    ? (asset?.images || [])
    : orderBy(asset?.images || [], v => v.default, "desc");
  
  const [open, setOpen] = useState(null);
  const defaultIndex = list.findIndex(v => v.default);
  const [index, setIndex] = useState(defaultIndex === -1 ? 0 : defaultIndex);
  const [addAttachmentShipmentItems] = useMutation(ADD_ATTACHMENT_SHIPMENT_ITEMS);
  const [removeAttachmentShipmentItems] = useMutation(REMOVE_ATTACHMENT_SHIPMENT_ITEMS);
  const [updateAttachmentShipmentItems] = useMutation(UPDATE_ATTACHMENT_SHIPMENT_ITEMS);
  const { setLoading: setFormLoading } = useLoading();
  const hasError = errors.photo && submitCount > 0;

  const handleOpen = () => {
    if (list.length > 0) {
      setOpen(true);
    }
  };

  const handleBulkCreateImages = async ({ variables = {}, onSuccess, onFailure }) => {
    setFormLoading(true);

    const { images, ...restVariables } = variables;
    const processedImages =
      images && images.length ? await compressImageFiles(images) : images;

    const response = await bulkCreateImages({
      variables: { ...restVariables, images: processedImages, assetId: asset?.id },
      onSuccess: async result => {
        if (shipmentItemId) {
          // Use the newly created images directly from the mutation response
          // instead of slicing asset.images which may not be properly ordered
          const newImages = result.images;

          await Promise.all(
            newImages.map((image, index) => {
              return addAttachmentShipmentItems({
                variables: {
                  url: image.url,
                  shipmentItemId,
                  pairId: +image.id,
                },
                refetchQueries: [
                  { query: GET_SHIPMENT_QUERY, variables: { id: params.get("id") } },
                ],
              });
            })
          );
        }
        notifySuccess("Images have been uploaded");
        onSuccess?.(result);
        setFormLoading(false);
      },
      onFailure: errors => {
        onFailure?.(errors);
        setFormLoading(false);
      },
      disableNotification: true,
    });

    return response;
  };

  const handleUpload = async images => {
    setLoading(true);
    await handleBulkCreateImages({
      variables: { assetId: asset.id, images },
      onFailure: errors => {
        notifyError(errors);
        setLoading(false);
      },
      onSuccess: () => {
        setLoading(false);
      },
    });
  };

  const handleRemove = (data, currentImage) => {
    setIndex(0);
    if (shipmentItemId && currentImage.pairId) {
      removeImage({ ...data, variables: { id: currentImage.pairId } });
      removeAttachmentShipmentItems({
        variables: { id: data.variables.id, shipmentItemId },
        refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: params.get("id") } }],
      });
    } else {
      removeImage(data);
    }
  };

  const handleUpdateImage = (data, currentImage) => {
    if (shipmentItemId && currentImage.pairId) {
      updateImage({ ...data, variables: { ...data.variables, id: currentImage.pairId } });
      updateAttachmentShipmentItems({
        variables: data.variables,
        refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: params.get("id") } }],
      });
    } else {
      updateImage(data);
    }
  };

  const handleDefaultUpload = e => {
    const files = Array.from(e.target.files);
    if (files.length) {
      handleUpload(files);
    }
  };

  return (
    <Box px={{ md: 5, xs: 2 }}>
      <Box width="100%" sx={{ aspectRatio: "5/4" }}>
        {loading ? (
          <Skeleton variant="rectangular" height="100%" width="100%" />
        ) : (
          <Box position="relative" onClick={handleOpen}>
            {list.length === 0 && (
              <ImageDND 
                onDrop={handleUpload} 
                helperText="Add item photos here — include labels or markings"
              />
            )}
            <ImagesSlider list={list} index={index} onChangeIndex={setIndex} />
          </Box>
        )}
      </Box>
      {open && list[index] && (
        <ImageModal
          open
          closeOnSave={true}
          selectedId={list[index].id}
          asset={asset}
          images={list}
          onClose={() => setOpen(false)}
          onRemove={handleRemove}
          onUpdate={handleUpdateImage}
          onCreate={handleBulkCreateImages}
        />
      )}
      <ImageUploadButtons onUpload={handleDefaultUpload} hasError={hasError} idPrefix="item" />
      {hasError && (
        <Box mt={1} display="flex" justifyContent="center">
          <FormHelperText error>{errors.photo}</FormHelperText>
        </Box>
      )}
    </Box>
  );
}
